import { Controller } from "@hotwired/stimulus"
import * as UpChunk from '@mux/upchunk'

export default class extends Controller {
  static targets = ["FileInput", "ProgressBar", "StatusText", "UploadContainer"]
  static values = {
    resourceType: String,
    resourceId: String,
    uploadUrl: String
  }

  connect() {
    this.FileInputTarget.addEventListener('change', this.handleFileSelect.bind(this))
  }

  callControllerMethod(controllerAndMethod, payload = null) {
    const parts = controllerAndMethod.split('->')
    const controller = parts[0]
    const method = parts[1]
    const controllerElement = document.querySelector(`[data-controller*="${controller}"]`)
    const controllerInstance = this.application.getControllerForElementAndIdentifier(controllerElement, controller)
    if (controllerInstance && payload) {
      controllerInstance[method](payload)
    }
  }

  getCsrfToken() {
    const token = document.querySelector('meta[name="csrf-token"]')
    return token ? token.content : ''
  }

  async handleFileSelect(event) {
    const file = event.target.files[0]
    if (!file) return

    // Check if it's a video file
    if (!file.type.startsWith('video/')) {
      alert('Please select a video file')
      return
    }

    // Check file size (500MB limit)
    const maxSize = 500 * 1024 * 1024 // 500MB
    if (file.size > maxSize) {
      alert('Video file must be less than 500MB')
      return
    }

    // Request Mux upload URL
    const uploadUrl = await this.requestMuxUploadUrl()
    if (!uploadUrl) {
      alert('Failed to get upload URL')
      return
    }

    // Start upload
    this.uploadToMux(file, uploadUrl)
  }

  async requestMuxUploadUrl() {
    try {
      const formData = new FormData()
      formData.append('request_mux_upload', 'true')
      formData.append(`${this.resourceTypeValue}_id`, this.resourceIdValue)

      const response = await fetch('/admin/attachments', {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRF-Token': this.getCsrfToken()
        }
      })

      if (!response.ok) throw new Error('Failed to get upload URL')

      const data = await response.json()
      return data.mux_upload_url
    } catch (error) {
      console.error('Error requesting Mux upload URL:', error)
      return null
    }
  }

  uploadToMux(file, uploadUrlData) {
    // Show upload container
    this.UploadContainerTarget.classList.remove('hidden')
    this.StatusTextTarget.textContent = 'Uploading video...'

    // Create the upload using UpChunk
    const upload = UpChunk.createUpload({
      endpoint: uploadUrlData.upload_url,
      file: file,
      chunkSize: 256 * 1024, // 256KB chunks as recommended

      // Progress callback
      onProgress: (progress) => {
        const percentage = Math.round(progress.detail * 100)
        this.ProgressBarTarget.style.width = `${percentage}%`
        this.StatusTextTarget.textContent = `Uploading: ${percentage}%`
      },

      // Success callback
      onSuccess: () => {
        this.StatusTextTarget.textContent = 'Upload complete! Processing video...'
        this.createAttachmentRecord(uploadUrlData.upload_id, file)
      },

      // Error callback
      onError: (error) => {
        console.error('Upload error:', error)
        this.StatusTextTarget.textContent = 'Upload failed. Please try again.'
        this.ProgressBarTarget.style.width = '0%'
      }
    })
  }

  async createAttachmentRecord(uploadId, file) {
    try {
      const filename = file ? file.name : ''
      const formData = new FormData()
      formData.append('attachment[mux_upload_id]', uploadId)
      formData.append('attachment[user_filename]', filename)
      formData.append('attachment[video_status]', 'uploading')
      if (file) {
        formData.append('attachment[file_size_in_bytes]', file.size)
        if (file.type) {
          formData.append('attachment[original_file_mime_type]', file.type)
        }
      }
      formData.append(`${this.resourceTypeValue}_id`, this.resourceIdValue)

      const response = await fetch('/admin/attachments', {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRF-Token': this.getCsrfToken()
        }
      })

      if (!response.ok) throw new Error('Failed to create attachment record')

      const data = await response.json()

      // Call file-list controller to add the attachment to the list
      this.callControllerMethod('file-list->onAppendListItem', data.attachments[0])

      // Reset UI
      this.StatusTextTarget.textContent = 'Video uploaded successfully!'
      setTimeout(() => {
        this.UploadContainerTarget.classList.add('hidden')
        this.ProgressBarTarget.style.width = '0%'
        this.FileInputTarget.value = ''
      }, 2000)

    } catch (error) {
      console.error('Error creating attachment record:', error)
      this.StatusTextTarget.textContent = 'Failed to save video. Please try again.'
    }
  }
}
